import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ArrowRight, Lock, MoreHorizontal, TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import type {
  ClinicalSummaryItem,
  ClinicalSummaryAttention,
} from "@/api/hooks";
import type { AdoptionDecision } from "@/lib/adoptions";
import { ADOPTION_STATE_LABEL, LOCK_REASON_LABEL } from "@/lib/labels";
import { libraryAssetHref } from "@/lib/librarySections";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { IconButton } from "@/ui/IconButton";
import { Tooltip } from "@/ui/Tooltip";

/**
 * The compact clinical review presentation for one Project section (#488).
 *
 * ## What replaced what
 *
 * Until this component, a clinical section's body was `SectionFieldList` — the
 * effective projection rendered as a two-column grid of one read-only control
 * per field, each labelled with its dotted address and carrying the field's
 * value as text. On the ratified base pack that is 171 rows for `summary`
 * alone, and for every section it is prompt text and `JSON.stringify` output
 * presented as the primary clinical review surface.
 *
 * #487 built the contract that replaces it and nothing consumed it. Each item
 * here is one `ClinicalSummaryItem`: a clinician-readable name, the family it
 * belongs to, the exact adopted version, where it came from, and its lock and
 * attention state. The contract's own description is the boundary this
 * component relies on — "never carries projection paths or content" — so there
 * is no value here that could be a prompt, and none that could be a path.
 *
 * ## One compact list row per bounded clinical item
 *
 * The amendment on #488 asks for the compact bounded-item hierarchy shown in
 * the canonical reference, not a smaller diagnostics grid. Each service item
 * is therefore one list row: name, a short supply/provenance line, meaningful
 * attention or lock badges, and its actions. Opaque source ids, base revision
 * hashes, and repeated labels do not belong on this primary Project route.
 * The exact adopted version remains in the supply line and every adoption
 * decision names the exact version on its control and confirmation.
 *
 * ## PROP-DRIVEN, deliberately
 *
 * The same arrangement `SectionAdoptionBlock` and `AdoptionRow` use: this
 * component reads no query, so every state below — including combinations no
 * live lane produces, such as an adopted item whose pin is behind the latest
 * while also locked by the safety floor — is reachable in a test without a
 * service standing in a particular condition. `SectionReview` is the query
 * holder.
 *
 * ## The row carries the canonical actions, and still writes nothing
 *
 * Each independently adopted BG set is one bounded row with its own canonical
 * ellipsis actions. The list length comes from stored adoptions; neither the
 * reference's example count nor the seeded roster is mirrored here. The row
 * action belongs on the item being reviewed while the panel above continues to
 * answer the broader "what supplies this section" question.
 *
 * WHAT DID NOT CHANGE is the thing that reading was protecting. These controls
 * only call `onDecide`. The query holder opens `AdoptionDecisionDialog` —
 * #372's confirmation, reused unchanged — which computes the whole-set
 * `PUT /adoptions` replacement, names the exact immutable version being
 * adopted and any pin it would displace, and submits under the
 * `adoptionsVersion` it loaded. No adoption API is added, no second place
 * computes a pin set, and nothing here selects "latest" on a clinician's
 * behalf: the version an Adopt would write is named on the control before it is
 * pressed and again in the dialog.
 *
 * The panel keeps its own controls. It is the surface for the two things a row
 * cannot express — a library item this response does not name at all, and the
 * case where the bounded read could not be made — so removing them would delete
 * the capability #489's second round was fixed for. Every control on both
 * surfaces opens the same dialog.
 *
 * WHERE THE ROW WINS, IT WINS FOR BASE ITEMS TOO (#570 round 3). "An item the
 * section does not report" is NOT the same set as "an item the project does not
 * pin": the service renders an unadopted seed-catalog entry as a
 * `supplyState: "base"` row, and that row offers the same adoption the panel's
 * candidate list would have. The panel is given the ids these rows cover and
 * drops them from BOTH of its halves, so one asset is one control wherever the
 * bounded response reaches it.
 *
 * ADR 0001 still decides what is RENDERED: a control that cannot act is not
 * shown as though it can. An item already at the library's head offers no
 * Adopt, an item this project does not pin offers no removal, and a refusal
 * the service reported disables the control and states itself.
 */

/** What one row may offer, decided by the query holder and never here. */
export type SectionReviewItemActions = {
  /**
   * The exact immutable version an Adopt would write, or `null` when none can
   * be named — an unread catalog, or an item the library does not hold.
   *
   * Never derived here and never "the latest": it is the service's own
   * `latestVersion` for an adopted item and the catalog's own for a base one.
   */
  readonly adoptVersion: string | null;
  /** The service's reason for refusing that adoption, or `null`. */
  readonly adoptRefusal: string | null;
  /** Whether this project holds a pin on this item. */
  readonly held: boolean;
  /** The service's reason for refusing removal, or `null`. */
  readonly removeRefusal: string | null;
};

/**
 * The row controls, or absent where none may be offered.
 *
 * ABSENT IS A STATE, exactly as it is on `SectionAdoptionBlock`. Every decision
 * is a whole-set replacement against a pin set and a catalog, so a control
 * offered over either of those unread could only open a dialog that fails
 * closed — the affordance ADR 0001 forbids.
 */
export type SectionReviewActions = {
  readonly onDecide: (decision: AdoptionDecision) => void;
  readonly byItemId: ReadonlyMap<string, SectionReviewItemActions>;
};

const ATTENTION_LABEL = {
  outdated_adoption: "Newer version available",
  local_override: "Overridden in this project",
} as const satisfies Record<ClinicalSummaryAttention, string>;

/**
 * What a row says while its own read is still settling (#618).
 *
 * The BADGE is `ADOPTION_STATE_LABEL.converging` — the block's own word, read
 * from the block's own map rather than repeated as a literal, because the whole
 * point of the state is that the two surfaces speak one vocabulary about it.
 *
 * The SENTENCE is this row's, and it names the supply without naming a version:
 * the version this response carries may be the one a moved pin set has already
 * left behind, and it is exactly the value whose confident rendering produced
 * the audited `Adopted v5` beside the block's `Adopted v6`. What is withheld is
 * the CLAIM, not the fact that something is unsettled — silence would leave a
 * clinician reading a row that quietly stopped offering its control.
 */
const CONVERGING_NOTE =
  "waiting for this read to settle against this project's current pin set";

const LOCK_STATE_TEXT = {
  editable: "Not locked",
  partially_locked: "Partly locked",
  locked: "Locked",
} as const satisfies Record<ClinicalSummaryItem["lock"]["state"], string>;

function supplySummary(item: ClinicalSummaryItem, converging: boolean): string {
  if (converging) {
    // NO VERSION IN ANY ARM, including the overridden one: `overridden` is a
    // statement about content this project changed away from a version, and
    // the version is the part that cannot be vouched for right now.
    return item.adoptedVersion === null
      ? `Inherited from base · ${CONVERGING_NOTE}`
      : `Adopted from library · ${CONVERGING_NOTE}`;
  }
  if (item.family === "background_set" && item.adoptedVersion !== null) {
    return "Adopted from library";
  }
  if (item.provenance === "overridden") {
    return item.adoptedVersion === null
      ? "Inherited from base · overridden in this project"
      : `Adopted ${item.adoptedVersion} from library · overridden in this project`;
  }
  if (item.adoptedVersion !== null) {
    return `Adopted ${item.adoptedVersion} from library`;
  }
  return "Inherited from base";
}

export function SectionReviewItem({
  item,
  converging = false,
  control,
  actions,
  onDecide,
}: {
  item: ClinicalSummaryItem;
  /**
   * A row-scoped control, rendered before the row's own actions, or absent
   * (#855).
   *
   * The Lovable Pathways row carries an `Always assessed` switch in exactly
   * this position — between the row's badges and its ellipsis menu — and the
   * ratified contract asks for that row switch to be preserved rather than
   * moved into a separate editor. The control is the CALLER's: this component
   * still reads no query and decides nothing, and every other section passes
   * none.
   */
  control?: ReactNode;
  /**
   * Whether this row describes an earlier pin set than the loaded one (#618).
   *
   * Decided in `sectionReviewRows`, never here: the row's version and its
   * attention flags are both claims about currency, and while the two reads
   * disagree neither can be made.
   *
   * The DECISION controls go with them, because the query holder passes no
   * `actions` in that window — so `ItemActions` still renders, still carrying
   * `View in Clinical library`, and offers neither an Adopt nor a removal. That
   * split is deliberate: navigation to the authoritative library asset depends
   * on no pin set at all, while every decision is a whole-set replacement
   * against the very read the screen is waiting to settle.
   */
  converging?: boolean;
  /** This item's own controls, or absent where none may be offered. */
  actions?: SectionReviewItemActions | undefined;
  onDecide?: ((decision: AdoptionDecision) => void) | undefined;
}) {
  return (
    <li className="border-b border-border bg-card px-4 py-3 last:border-b-0">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="min-w-0 flex-1">
          {/*
            `wrap-anywhere`: a #612 recovery row for a pin the catalog did not
            name carries the opaque asset id as its name (`storedAdoptionRows`),
            and an `OpaqueId` may run to 128 unbroken characters — wider than
            the 390px card. `overflow-wrap: anywhere` breaks only a token that
            cannot otherwise fit, so a clinical name wraps at its spaces as it
            always did.
          */}
          <p className="wrap-anywhere text-sm font-semibold text-foreground">
            {item.name}
          </p>
          <p className="text-xs text-muted-foreground">
            {supplySummary(item, converging)}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/*
            Attention reaches the clinician as an icon AND its words, never as
            colour alone — the rule `FindingItem` follows for severity.
          */}
          {converging ? (
            // INSTEAD OF the attention flags, not beside them. Every member of
            // that vocabulary is a currency claim — "Newer version available"
            // is the audited row's own words — and a settling read supports
            // none of them. The badge tone matches the block's `accent`, so the
            // two surfaces do not read as two different kinds of event.
            <Badge tone="accent">{ADOPTION_STATE_LABEL.converging}</Badge>
          ) : (
            item.attention.map((flag) => (
              <Badge key={flag} tone="warning">
                <TriangleAlert aria-hidden="true" className="mr-1 h-3 w-3" />
                {ATTENTION_LABEL[flag]}
              </Badge>
            ))
          )}
          {item.lock.state !== "editable" ? (
            <Badge tone="neutral">
              <Lock aria-hidden="true" className="mr-1 h-3 w-3" />
              {item.lock.reasons.length > 0
                ? item.lock.reasons
                    .map((reason) => LOCK_REASON_LABEL[reason] ?? reason)
                    .join(", ")
                : LOCK_STATE_TEXT[item.lock.state]}
            </Badge>
          ) : null}

          {control}

          <ItemActions item={item} actions={actions} onDecide={onDecide} />
        </div>
      </div>
    </li>
  );
}

/**
 * The canonical row actions: an Adopt naming its exact version, and a named
 * ellipsis menu that always links the item back to its Clinical library page.
 *
 * WHY ADOPT IS SOMETIMES ABSENT. It is offered when a version exists that this
 * project does not already hold — the library's head for an item pinned behind
 * it, and the catalog's head for one supplied by the base pack that the project
 * has never pinned. An item already at that version has nothing to adopt, and a
 * disabled "Adopt" beside it would be a control that can never act.
 *
 * WHY THE MENU IS ALWAYS PRESENT. Project review is a view of shared clinical
 * content, so every bounded item has a read-only way back to its authoritative
 * Clinical library asset. That navigation does not depend on a project's pin
 * state, on `draft.write`, or on the two reads needed to compute a whole-set
 * adoption replacement. The removal entry remains conditional on a held pin.
 *
 * A refusal the service reported is not an absence: the control renders,
 * disabled, carrying the service's own sentence. The distinction is the one
 * `AdoptedItemControls` draws — "cannot be offered" and "may not be done by
 * you, and here is why" are different things to tell a clinician.
 */
function ItemActions({
  item,
  actions,
  onDecide,
}: {
  item: ClinicalSummaryItem;
  actions?: SectionReviewItemActions | undefined;
  onDecide?: ((decision: AdoptionDecision) => void) | undefined;
}) {
  const adoptVersion = actions?.adoptVersion ?? null;
  const canAdopt =
    actions !== undefined &&
    onDecide !== undefined &&
    adoptVersion !== null &&
    adoptVersion !== item.adoptedVersion;
  const libraryHref = libraryAssetHref(item.family, item.id);

  return (
    <div className="flex items-center gap-1">
      {canAdopt ? (
        <Tooltip
          content={
            actions.adoptRefusal ??
            (item.adoptedVersion === null
              ? `Adopt ${item.name} at ${adoptVersion} into this project`
              : `Move this project's pin on ${item.name} from ${item.adoptedVersion} to ${adoptVersion} in one replacement`)
          }
        >
          <Button
            size="sm"
            // The item is in the NAME, not only in the visible text. Fifteen
            // rows would otherwise be fifteen controls called "Adopt v5" in a
            // screen-reader control listing, which is the defect the ellipsis
            // beside it is labelled to avoid. An explicit label rather than an
            // `sr-only` suffix, because the accessible name computed from two
            // text nodes drops the whitespace between them and reads
            // "Adopt v5of Chest pain triage" — measured, not assumed.
            aria-label={`Adopt ${adoptVersion} of ${item.name}`}
            disabled={actions.adoptRefusal !== null}
            onClick={() =>
              onDecide({
                mode: "pin",
                assetId: item.id,
                version: adoptVersion,
              })
            }
          >
            {/*
              The exact version, on the control itself. "Adopt" alone would
              leave the clinician to infer which version they were about to
              pin, and the criterion is that adoption never silently selects
              latest.
            */}
            {`Adopt ${adoptVersion}`}
          </Button>
        </Tooltip>
      ) : null}

      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          {/*
            NAMED, per the amendment's "named ellipsis buttons": `IconButton`
            makes the accessible name mandatory in the type system, and it
            carries the item so a screen-reader control listing of a
            fifteen-item section is not fifteen identical "More actions".
          */}
          <IconButton
            label={`More actions for ${item.name}`}
            icon={<MoreHorizontal className="h-4 w-4" />}
          />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={6}
            className="z-50 min-w-56 max-w-[calc(100vw-2rem)] rounded-md border border-border bg-popover p-1 shadow-md"
          >
            <DropdownMenu.Item asChild>
              <Link
                to={libraryHref}
                // The item is in the NAME (#615: several rows must not read as
                // identical library actions in a control listing), and the
                // visible text is the name's PREFIX, not scattered through it
                // — WCAG 2.5.3 requires the text a sighted user sees to be
                // contained in what speech input matches against, and the
                // ratified model keeps the visible item exactly `View in
                // Clinical library`. Same shape as the Adopt control above.
                aria-label={`View in Clinical library: ${item.name}`}
                className="flex cursor-pointer items-center justify-between gap-3 rounded-md px-3 py-2 text-sm text-foreground outline-none data-[highlighted]:bg-secondary"
              >
                <span>View in Clinical library</span>
                <ArrowRight aria-hidden="true" className="h-4 w-4" />
              </Link>
            </DropdownMenu.Item>
            {actions?.held && onDecide ? (
              <DropdownMenu.Item
                disabled={actions.removeRefusal !== null}
                // Deferred past this event-loop turn for the reason `UserMenu`
                // records: Radix closes the menu on select and restores focus
                // to the trigger, and opening a dialog synchronously makes the
                // two focus moves race.
                onSelect={() => {
                  setTimeout(
                    () => onDecide({ mode: "unpin", assetId: item.id }),
                    0,
                  );
                }}
                className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground outline-none data-[highlighted]:bg-secondary data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50"
              >
                Remove from project
              </DropdownMenu.Item>
            ) : null}
            {actions?.held && actions.removeRefusal ? (
              <p className="px-3 pb-2 pt-1 text-xs text-muted-foreground">
                {actions.removeRefusal}
              </p>
            ) : null}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}

/**
 * One named group of rows, for a section whose rows mean different things
 * depending on which group they are in (#855).
 *
 * The Lovable Pathways screen is two such groups — `ALWAYS ASSESSED` and
 * `DYNAMIC — ASSESSED BASED ON MAIN CONCERN` — and the difference between them
 * is a runtime fact, not a sort order: a pathway in the first is assessed for
 * every patient, and one in the second is chosen by classification. The
 * grouping is therefore the CALLER's, derived from the service's own selection
 * model, and this component only lays it out.
 */
export type SectionReviewGroup = {
  readonly key: string;
  readonly label: string;
  /** What being in this group MEANS at runtime. Lovable renders one line. */
  readonly description?: string;
  /** A group-level action, at the right of its heading. */
  readonly action?: ReactNode;
  /** Rendered inside the group, above its rows. */
  readonly lead?: ReactNode;
  /** What this group says when it holds no rows. Never silence. */
  readonly empty: string;
};

export type SectionReviewGrouping = {
  readonly groups: readonly SectionReviewGroup[];
  /**
   * Which group a row belongs to. A key this grouping does not declare — and
   * `null` — sends the row to the ungrouped list below the groups, which is
   * where a pin the selection read could not place still renders WITH its
   * actions. Dropping such a row would hide a pin a clinician has to be able to
   * remove.
   */
  readonly groupKeyOf: (item: ClinicalSummaryItem) => string | null;
  /** This row's own control, or absent. */
  readonly controlOf?: (item: ClinicalSummaryItem) => ReactNode;
  /** A line above the groups: Lovable's `3 pathways · 1 always assessed, 2 dynamic`. */
  readonly summary?: ReactNode;
  /** Rendered below every group. #855's routing-order disclosure. */
  readonly footer?: ReactNode;
  /** What the ungrouped list is called, when one is needed. */
  readonly ungroupedLabel: string;
  /** Why a row is ungrouped. Rendered only when one is. */
  readonly ungroupedDescription: string;
};

export function SectionReviewList({
  items,
  sectionLabel,
  converging = false,
  actions,
  grouping,
}: {
  items: readonly ClinicalSummaryItem[];
  sectionLabel: string;
  /**
   * Whether these rows describe an earlier pin set than the loaded one (#618).
   *
   * A property of the ROW SET rather than of one row: the whole list came out
   * of one composition, so either all of it describes the loaded pin set or
   * none of it does.
   */
  converging?: boolean;
  /** The row controls, or absent where none may be offered. */
  actions?: SectionReviewActions | undefined;
  /** Named groups for these rows, or absent for one flat list (#855). */
  grouping?: SectionReviewGrouping | undefined;
}) {
  if (grouping === undefined) {
    return (
      <RowList items={items} label={`${sectionLabel} items`} converging={converging} actions={actions} />
    );
  }

  const byGroup = new Map<string, ClinicalSummaryItem[]>(
    grouping.groups.map((group) => [group.key, []]),
  );
  const ungrouped: ClinicalSummaryItem[] = [];
  for (const item of items) {
    const key = grouping.groupKeyOf(item);
    const bucket = key === null ? undefined : byGroup.get(key);
    if (bucket === undefined) {
      ungrouped.push(item);
      continue;
    }
    bucket.push(item);
  }

  return (
    <div className="space-y-4" data-slot="section-review-groups">
      {grouping.summary}

      {grouping.groups.map((group) => {
        const rows = byGroup.get(group.key) ?? [];
        return (
          <section key={group.key} data-slot={`group-${group.key}`} className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
              <div className="min-w-0">
                {/*
                  `h3` under the pane's own `h2`: the groups are subsections of
                  one section, and a screen-reader user navigating by heading
                  reaches them in the order the rows are read.
                */}
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {group.label}
                </h3>
                {group.description ? (
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {group.description}
                  </p>
                ) : null}
              </div>
              {group.action}
            </div>

            {group.lead}

            {rows.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border px-4 py-3 text-xs text-muted-foreground">
                {group.empty}
              </p>
            ) : (
              <RowList
                items={rows}
                label={`${group.label} — ${sectionLabel} items`}
                converging={converging}
                actions={actions}
                controlOf={grouping.controlOf}
              />
            )}
          </section>
        );
      })}

      {ungrouped.length > 0 ? (
        <section data-slot="group-ungrouped" className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {grouping.ungroupedLabel}
          </h3>
          <p className="text-xs text-muted-foreground">
            {grouping.ungroupedDescription}
          </p>
          <RowList
            items={ungrouped}
            label={`${grouping.ungroupedLabel} — ${sectionLabel} items`}
            converging={converging}
            actions={actions}
          />
        </section>
      ) : null}

      {grouping.footer}
    </div>
  );
}

/** The one `<ul>` every arm above renders through, grouped or not. */
function RowList({
  items,
  label,
  converging,
  actions,
  controlOf,
}: {
  items: readonly ClinicalSummaryItem[];
  label: string;
  converging: boolean;
  actions?: SectionReviewActions | undefined;
  controlOf?: ((item: ClinicalSummaryItem) => ReactNode) | undefined;
}) {
  return (
    <ul
      aria-label={label}
      className="overflow-hidden rounded-lg border border-border bg-card"
    >
      {items.map((item, index) => {
        const control = controlOf?.(item);
        return (
          <SectionReviewItem
            key={`${item.id}:${index}`}
            item={item}
            converging={converging}
            {...(control !== undefined && control !== null ? { control } : {})}
            {...(actions
              ? {
                  actions: actions.byItemId.get(item.id),
                  onDecide: actions.onDecide,
                }
              : {})}
          />
        );
      })}
    </ul>
  );
}
